import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  InputAdornment,
  MenuItem,
  Snackbar,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
// Иконки для кастомных цен, акций и дополнительных услуг
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EventIcon from '@mui/icons-material/Event';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import DiscountIcon from '@mui/icons-material/Discount';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SpaIcon from '@mui/icons-material/Spa';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import PetsIcon from '@mui/icons-material/Pets';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PoolIcon from '@mui/icons-material/Pool';
import HotTubIcon from '@mui/icons-material/HotTub';
import FireplaceIcon from '@mui/icons-material/Fireplace';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WifiIcon from '@mui/icons-material/Wifi';
import TvIcon from '@mui/icons-material/Tv';
import KitchenIcon from '@mui/icons-material/Kitchen';
import MicrowaveIcon from '@mui/icons-material/Microwave';
import CoffeeIcon from '@mui/icons-material/Coffee';
import IronIcon from '@mui/icons-material/Iron';
import WeekendIcon from '@mui/icons-material/Weekend';
import BedIcon from '@mui/icons-material/Bed';
import ShowerIcon from '@mui/icons-material/Shower';
import BathtubIcon from '@mui/icons-material/Bathtub';
import ExternalLinks from '../../components/admin/ExternalLinks';

// External platform icons
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import InstagramIcon from '@mui/icons-material/Instagram';
import SecurityIcon from '@mui/icons-material/Security';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import SmokeFreeIcon from '@mui/icons-material/SmokeFree';
import AccessibleIcon from '@mui/icons-material/Accessible';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import NightlifeIcon from '@mui/icons-material/Nightlife';
import SportsBarIcon from '@mui/icons-material/SportsBar';
import CasinoIcon from '@mui/icons-material/Casino';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
// Компоненты определены внутри файла
import HourlyPricesByGuests from '../../components/admin/HourlyPricesByGuests';
import { useAuthStore } from '../../store/authStore';


// Property type options
const propertyTypes = [
  'Коттедж',
  'Баня',
  'Дача',
  'Апартаменты',
  'Гостевой дом',
  'Загородный дом'
];

// Rental type options
const rentalTypes = [
  { value: 'hourly', label: 'Почасовая' },
  { value: 'daily', label: 'Посуточная' }
];

// Available icons for custom prices and promotions
const availableIcons = [
  // Цены и акции
  { icon: <LocalOfferIcon />, name: 'LocalOffer', label: 'Ценник' },
  { icon: <CelebrationIcon />, name: 'Celebration', label: 'Праздник' },
  { icon: <EventIcon />, name: 'Event', label: 'Событие' },
  { icon: <StarIcon />, name: 'Star', label: 'Звезда' },
  { icon: <FavoriteIcon />, name: 'Favorite', label: 'Избранное' },
  { icon: <CardGiftcardIcon />, name: 'Gift', label: 'Подарок' },
  { icon: <DiscountIcon />, name: 'Discount', label: 'Скидка' },
  { icon: <NewReleasesIcon />, name: 'NewReleases', label: 'Новинка' },
  { icon: <EmojiEventsIcon />, name: 'EmojiEvents', label: 'Награда' },
  
  // Удобства и услуги
  { icon: <SpaIcon />, name: 'Spa', label: 'Спа' },
  { icon: <LocalBarIcon />, name: 'LocalBar', label: 'Бар' },
  { icon: <RestaurantIcon />, name: 'Restaurant', label: 'Ресторан' },
  { icon: <DirectionsCarIcon />, name: 'DirectionsCar', label: 'Трансфер' },
  { icon: <CleaningServicesIcon />, name: 'CleaningServices', label: 'Уборка' },
  { icon: <LocalLaundryServiceIcon />, name: 'LocalLaundryService', label: 'Прачечная' },
  { icon: <PetsIcon />, name: 'Pets', label: 'Животные' },
  { icon: <ChildCareIcon />, name: 'ChildCare', label: 'Детский уход' },
  { icon: <FitnessCenterIcon />, name: 'FitnessCenter', label: 'Фитнес' },
  { icon: <PoolIcon />, name: 'Pool', label: 'Бассейн' },
  { icon: <HotTubIcon />, name: 'HotTub', label: 'Джакузи' },
  { icon: <FireplaceIcon />, name: 'Fireplace', label: 'Камин' },
  { icon: <OutdoorGrillIcon />, name: 'OutdoorGrill', label: 'Гриль' },
  { icon: <BeachAccessIcon />, name: 'BeachAccess', label: 'Пляж' },
  { icon: <AcUnitIcon />, name: 'AcUnit', label: 'Кондиционер' },
  { icon: <WifiIcon />, name: 'Wifi', label: 'Wi-Fi' },
  { icon: <TvIcon />, name: 'Tv', label: 'Телевизор' },
  { icon: <KitchenIcon />, name: 'Kitchen', label: 'Кухня' },
  { icon: <MicrowaveIcon />, name: 'Microwave', label: 'Микроволновка' },
  { icon: <CoffeeIcon />, name: 'Coffee', label: 'Кофе' },
  { icon: <IronIcon />, name: 'Iron', label: 'Утюг' },
  { icon: <WeekendIcon />, name: 'Weekend', label: 'Диван' },
  { icon: <BedIcon />, name: 'Bed', label: 'Кровать' },
  { icon: <ShowerIcon />, name: 'Shower', label: 'Душ' },
  { icon: <BathtubIcon />, name: 'Bathtub', label: 'Ванна' },
  { icon: <SecurityIcon />, name: 'Security', label: 'Охрана' },
  { icon: <LocalParkingIcon />, name: 'LocalParking', label: 'Парковка' },
  { icon: <SmokingRoomsIcon />, name: 'SmokingRooms', label: 'Курение разрешено' },
  { icon: <SmokeFreeIcon />, name: 'SmokeFree', label: 'Курение запрещено' },
  { icon: <AccessibleIcon />, name: 'Accessible', label: 'Доступно для инвалидов' },
  { icon: <ChildFriendlyIcon />, name: 'ChildFriendly', label: 'Для детей' },
  
  // Развлечения
  { icon: <NightlifeIcon />, name: 'Nightlife', label: 'Ночная жизнь' },
  { icon: <SportsBarIcon />, name: 'SportsBar', label: 'Спорт-бар' },
  { icon: <CasinoIcon />, name: 'Casino', label: 'Казино' },
  { icon: <MusicNoteIcon />, name: 'MusicNote', label: 'Музыка' },
  { icon: <SportsEsportsIcon />, name: 'SportsEsports', label: 'Игры' },
  { icon: <SportsFootballIcon />, name: 'SportsFootball', label: 'Футбол' },
  { icon: <SportsTennisIcon />, name: 'SportsTennis', label: 'Теннис' },
  { icon: <SportsBasketballIcon />, name: 'SportsBasketball', label: 'Баскетбол' },
  { icon: <SportsVolleyballIcon />, name: 'SportsVolleyball', label: 'Волейбол' },
];

// TabPanel component for tab content
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rental-type-tabpanel-${index}`}
      aria-labelledby={`rental-type-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Time fields component (for daily rental only)
const TimeFields = ({ control, errors }) => (
  <>
    <Grid item xs={12} md={6}>
      <Controller
        name="checkInTime"
        control={control}
        rules={{ required: 'Время заселения обязательно' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Время заселения"
            type="time"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccessTimeIcon />
                </InputAdornment>
              ),
            }}
            error={!!errors.checkInTime}
            helperText={errors.checkInTime?.message || 'Например: 14:00'}
          />
        )}
      />
    </Grid>
    
    <Grid item xs={12} md={6}>
      <Controller
        name="checkOutTime"
        control={control}
        rules={{ required: 'Время выезда обязательно' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Время выезда"
            type="time"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccessTimeIcon />
                </InputAdornment>
              ),
            }}
            error={!!errors.checkOutTime}
            helperText={errors.checkOutTime?.message || 'Например: 12:00'}
          />
        )}
      />
    </Grid>
  </>
);

TimeFields.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};
// Custom prices component
const CustomPrices = ({ customPrices, updateCustomPrice, removeCustomPrice, addCustomPrice }) => (
  <>
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        Кастомные цены
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Укажите специальные условия и цены для объекта
      </Typography>
    </Grid>
    
    {customPrices.map((price, index) => (
      <Grid container item xs={12} spacing={2} key={`custom-price-${index}`}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Условие"
            value={price.condition}
            onChange={(e) => updateCustomPrice(index, 'condition', e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="Например: Новогодние праздники"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Цена"
            value={price.price}
            onChange={(e) => updateCustomPrice(index, 'price', Number(e.target.value))}
            fullWidth
            variant="outlined"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">₽</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel id={`custom-price-icon-label-${index}`}>Иконка</InputLabel>
            <Select
              labelId={`custom-price-icon-label-${index}`}
              value={price.icon || ''}
              onChange={(e) => updateCustomPrice(index, 'icon', e.target.value)}
              label="Иконка"
            >
              <MenuItem value=""><em>Без иконки</em></MenuItem>
              {availableIcons.map((icon) => (
                <MenuItem key={icon.name} value={icon.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1 }}>{icon.icon}</Box>
                    {icon.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="error" onClick={() => removeCustomPrice(index)}>
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    ))}
    
    <Grid item xs={12}>
      <Button
        startIcon={<AddIcon />}
        onClick={() => addCustomPrice({ condition: '', price: 0, icon: '' })}
        variant="outlined"
        color="primary"
      >
        Добавить кастомную цену
      </Button>
    </Grid>
  </>
);

CustomPrices.propTypes = {
  customPrices: PropTypes.array.isRequired,
  updateCustomPrice: PropTypes.func.isRequired,
  removeCustomPrice: PropTypes.func.isRequired,
  addCustomPrice: PropTypes.func.isRequired,
};

// Additional services component
const AdditionalServices = ({ services, updateService, removeService, addService }) => {
  return (
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        Дополнительные услуги
      </Typography>
      
      {services.map((service, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Описание услуги"
              value={service.description}
              onChange={(e) => updateService(index, { ...service, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Цена"
              type="number"
              value={service.price}
              onChange={(e) => updateService(index, { ...service, price: parseFloat(e.target.value) || 0 })}
              InputProps={{
                endAdornment: <InputAdornment position="end">₽</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id={`service-icon-label-${index}`}>Иконка</InputLabel>
              <Select
                labelId={`service-icon-label-${index}`}
                value={service.icon || ''}
                label="Иконка"
                onChange={(e) => updateService(index, { ...service, icon: e.target.value })}
                renderValue={(selected) => {
                  const selectedIcon = availableIcons.find(icon => icon.name === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {selectedIcon?.icon}
                      <Box sx={{ ml: 1 }}>{selectedIcon?.label}</Box>
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <em>Нет иконки</em>
                </MenuItem>
                {availableIcons.map((icon) => (
                  <MenuItem key={icon.name} value={icon.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {icon.icon}
                      <Box sx={{ ml: 1 }}>{icon.label}</Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              color="error" 
              onClick={() => removeService(index)}
              aria-label="Удалить услугу"
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => addService({ description: '', price: 0, icon: '' })}
        sx={{ mt: 1 }}
      >
        Добавить услугу
      </Button>
    </Grid>
  );
};

AdditionalServices.propTypes = {
  services: PropTypes.array.isRequired,
  updateService: PropTypes.func.isRequired,
  removeService: PropTypes.func.isRequired,
  addService: PropTypes.func.isRequired,
};



// Promotions component
const Promotions = ({ promotions, updatePromotion, removePromotion, addPromotion }) => (
  <>
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        Акции
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Укажите действующие акции и специальные предложения
      </Typography>
    </Grid>
    
    {promotions.map((promo, index) => (
      <Grid container item xs={12} spacing={2} key={`promotion-${index}`}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Название акции"
            value={promo.name}
            onChange={(e) => updatePromotion(index, { ...promo, name: e.target.value })}
            fullWidth
            variant="outlined"
            placeholder="Например: Скидка для молодоженов"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Описание"
            value={promo.description}
            onChange={(e) => updatePromotion(index, { ...promo, description: e.target.value })}
            fullWidth
            variant="outlined"
            placeholder="Условия акции"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            label="Цена по акции"
            value={promo.price}
            onChange={(e) => updatePromotion(index, { ...promo, price: Number(e.target.value) })}
            fullWidth
            variant="outlined"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">₽</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id={`promotion-icon-label-${index}`}>Иконка</InputLabel>
            <Select
              labelId={`promotion-icon-label-${index}`}
              value={promo.icon || ''}
              onChange={(e) => updatePromotion(index, { ...promo, icon: e.target.value })}
              label="Иконка"
            >
              <MenuItem value=""><em>Без иконки</em></MenuItem>
              {availableIcons.map((icon) => (
                <MenuItem key={icon.name} value={icon.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1 }}>{icon.icon}</Box>
                    {icon.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="error" onClick={() => removePromotion(index)}>
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    ))}
    
    <Grid item xs={12}>
      <Button
        startIcon={<AddIcon />}
        onClick={() => addPromotion({ name: '', description: '', price: 0, icon: '' })}
        variant="outlined"
        color="primary"
      >
        Добавить акцию
      </Button>
    </Grid>
  </>
);

Promotions.propTypes = {
  promotions: PropTypes.array.isRequired,
  updatePromotion: PropTypes.func.isRequired,
  removePromotion: PropTypes.func.isRequired,
  addPromotion: PropTypes.func.isRequired,
};



// Image upload component
const ImageUpload = ({
  mainImagePreview,
  additionalImagePreviews,
  existingImages,
  handleMainImageChange,
  handleAdditionalImagesChange,
  handleRemoveAdditionalImage,
  handleRemoveExistingImage,
  isEditMode
}) => (
  <>
    <Grid item xs={12}>
      <Typography variant="h6" gutterBottom>
        Изображения
      </Typography>
    </Grid>
    
    {/* Main image upload */}
    <Grid item xs={12} md={6}>
      <Button
        variant="outlined"
        component="label"
        fullWidth
        startIcon={<ImageIcon />}
        sx={{ height: '56px' }}
      >
        {mainImagePreview ? 'Изменить главное фото' : 'Загрузить главное фото'}
        <input
          type="file"
          hidden
          accept="image/*"
          onChange={handleMainImageChange}
        />
      </Button>
    </Grid>
    
    {/* Main image preview */}
    {mainImagePreview && (
      <Grid item xs={12}>
        <Card>
          <CardMedia
            component="img"
            height="200"
            image={mainImagePreview}
            alt="Главное изображение"
            sx={{ objectFit: 'contain' }}
          />
          <CardContent>
            <Typography variant="body2">Главное изображение объекта</Typography>
          </CardContent>
        </Card>
      </Grid>
    )}
    
    {/* Additional images upload */}
    <Grid item xs={12} md={6}>
      <Button
        variant="outlined"
        component="label"
        fullWidth
        startIcon={<AddIcon />}
        sx={{ height: '56px' }}
      >
        Добавить дополнительные фото
        <input
          type="file"
          hidden
          accept="image/*"
          multiple
          onChange={handleAdditionalImagesChange}
        />
      </Button>
    </Grid>
    
    {/* New additional images preview */}
    {additionalImagePreviews.length > 0 && (
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Новые дополнительные изображения:
        </Typography>
        <Grid container spacing={2}>
          {additionalImagePreviews.map((preview, index) => (
            <Grid item xs={12} sm={6} md={4} key={`new-${index}`}>
              <Card>
                <CardMedia
                  component="img"
                  height="150"
                  image={preview}
                  alt={`Дополнительное изображение ${index + 1}`}
                  sx={{ objectFit: 'contain' }}
                />
                <CardActions>
                  <Button 
                    size="small" 
                    color="error" 
                    startIcon={<DeleteIcon />}
                    onClick={() => handleRemoveAdditionalImage(index)}
                  >
                    Удалить
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    )}
    
    {/* Existing additional images */}
    {existingImages.length > 0 && (
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Существующие дополнительные изображения:
        </Typography>
        <Grid container spacing={2}>
          {existingImages.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} key={`existing-${image.id}`}>
              <Card>
                <CardMedia
                  component="img"
                  height="150"
                  image={image.imageUrl}
                  alt={`Дополнительное изображение ${index + 1}`}
                  sx={{ objectFit: 'contain' }}
                />
                <CardActions>
                  <Button 
                    size="small" 
                    color="error" 
                    startIcon={<DeleteIcon />}
                    onClick={() => handleRemoveExistingImage(image.id, index)}
                  >
                    Удалить
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    )}
  </>
);

ImageUpload.propTypes = {
  mainImagePreview: PropTypes.string,
  additionalImagePreviews: PropTypes.array.isRequired,
  existingImages: PropTypes.array.isRequired,
  handleMainImageChange: PropTypes.func.isRequired,
  handleAdditionalImagesChange: PropTypes.func.isRequired,
  handleRemoveAdditionalImage: PropTypes.func.isRequired,
  handleRemoveExistingImage: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};



// Video selection component
const VideoUpload = ({
  videoPreviews,
  existingVideos,
  handleVideoChange,
  handleRemoveVideo,
  handleRemoveExistingVideo,
  isEditMode,
  availableVideos,
  handleSelectExistingVideo,
  selectedVideos
}) => {
  const [openVideoSelector, setOpenVideoSelector] = useState(false);
  
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Видео
        </Typography>
      </Grid>
      
      {/* Video selection buttons */}
      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<VideocamIcon />}
            sx={{ height: '56px' }}
            onClick={() => setOpenVideoSelector(true)}
          >
            Выбрать существующие видео
          </Button>
        </Grid>
      </Grid>
      
      {/* Video selection dialog */}
      <Dialog 
        open={openVideoSelector} 
        onClose={() => setOpenVideoSelector(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Выберите видео для объекта</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {availableVideos && availableVideos.length > 0 ? (
              availableVideos.map((video) => {
                const isSelected = selectedVideos?.some(v => v.id === video.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={`video-${video.id}`}>
                    <Card 
                      sx={{
                        border: isSelected ? '2px solid #3498db' : 'none',
                        position: 'relative'
                      }}
                    >
                      {isSelected && (
                        <Chip 
                          label="Выбрано" 
                          color="primary" 
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        />
                      )}
                      <CardMedia
                        component="video"
                        height="200"
                        src={video.url || video.videoUrl}
                        poster={video.thumbnailUrl || video.thumbnail || undefined}
                        controls
                      />
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {video.title || video.caption || 'Без названия'}
                        </Typography>
                        {video.description && (
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {video.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="primary" display="block" sx={{ mt: 1 }}>
                          ID: {video.id}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          color={isSelected ? "error" : "primary"}
                          onClick={() => handleSelectExistingVideo(video, isSelected)}
                        >
                          {isSelected ? 'Удалить' : 'Выбрать'}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1" align="center">
                  Нет доступных видео
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideoSelector(false)} color="primary">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Selected videos */}
      {selectedVideos && selectedVideos.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Выбранные видео:
          </Typography>
          <Grid container spacing={2}>
            {selectedVideos.map((video, index) => (
              <Grid item xs={12} sm={6} md={4} key={`selected-video-${video.id}`}>
                <Card>
                  <CardMedia
                    component="video"
                    height="200"
                    src={video.url || video.videoUrl}
                    poster={video.thumbnailUrl || video.thumbnail || undefined}
                    controls
                  />
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {video.title || video.caption || 'Без названия'}
                    </Typography>
                    {video.description && (
                      <Typography variant="body2" color="textSecondary" noWrap>
                        {video.description}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      color="error" 
                      startIcon={<DeleteIcon />}
                      onClick={() => handleSelectExistingVideo(video, true)}
                    >
                      Удалить
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </>
  );
};

VideoUpload.propTypes = {
  videoPreviews: PropTypes.array.isRequired,
  existingVideos: PropTypes.array.isRequired,
  handleVideoChange: PropTypes.func.isRequired,
  handleRemoveVideo: PropTypes.func.isRequired,
  handleRemoveExistingVideo: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  availableVideos: PropTypes.array,
  handleSelectExistingVideo: PropTypes.func,
  selectedVideos: PropTypes.array
};

// Form actions component
const FormActions = ({ onCancel, onSubmit, isSubmitting, isEditMode }) => (
  <Grid item xs={12}>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
      <Button
        variant="outlined"
        onClick={onCancel}
        sx={{ mr: 2 }}
        disabled={isSubmitting}
      >
        Отмена
      </Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isSubmitting}
        onClick={onSubmit}
      >
        {isSubmitting ? (
          <CircularProgress size={24} />
        ) : isEditMode ? (
          'Сохранить изменения'
        ) : (
          'Создать объект'
        )}
      </Button>
    </Box>
  </Grid>
);

FormActions.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

// Price fields component
const PriceFields = ({ control, errors, rentalType }) => (
  <>
    <Grid item xs={12} md={rentalType === 'hourly' ? 6 : 4}>
      <Controller
        name={`pricePer${rentalType === 'hourly' ? 'Hour' : 'Day'}`}
        control={control}
        rules={{
          required: 'Цена обязательна',
          min: { value: 0, message: 'Цена не может быть отрицательной' },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label={rentalType === 'hourly' ? 'Цена за час (₽)' : 'Цена за сутки (₽)'}
            type="number"
            fullWidth
            error={!!errors[`pricePer${rentalType === 'hourly' ? 'Hour' : 'Day'}`]}
            helperText={errors[`pricePer${rentalType === 'hourly' ? 'Hour' : 'Day'}`]?.message}
            InputProps={{
              startAdornment: <InputAdornment position="start">₽</InputAdornment>,
            }}
          />
        )}
      />
    </Grid>
    
    {/* Prepayment field */}
    <Grid item xs={12} md={4}>
      <Controller
        name="prepayment"
        control={control}
        rules={{
          min: { value: 0, message: 'Не может быть отрицательной' },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Предоплата (₽)"
            type="number"
            fullWidth
            error={!!errors.prepayment}
            helperText={errors.prepayment?.message || 'Сумма предоплаты в рублях'}
            InputProps={{
              startAdornment: <InputAdornment position="start">₽</InputAdornment>,
            }}
            inputProps={{ min: 0 }}
          />
        )}
      />
    </Grid>

    {rentalType === 'daily' && (
      <>
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Цены по дням недели
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="mondayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в понедельник"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.mondayPrice}
                helperText={errors.mondayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="tuesdayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена во вторник"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.tuesdayPrice}
                helperText={errors.tuesdayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="wednesdayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в среду"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.wednesdayPrice}
                helperText={errors.wednesdayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="thursdayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в четверг"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.thursdayPrice}
                helperText={errors.thursdayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="fridayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в пятницу"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.fridayPrice}
                helperText={errors.fridayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="saturdayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в субботу"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.saturdayPrice}
                helperText={errors.saturdayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="sundayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в воскресенье"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.sundayPrice}
                helperText={errors.sundayPrice?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name="holidayPrice"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Цена в праздники"
                type="number"
                fullWidth
                inputProps={{ step: '100', min: '0' }}
                error={!!errors.holidayPrice}
                helperText={errors.holidayPrice?.message}
              />
            )}
          />
        </Grid>
      </>
    )}
  </>
);

PriceFields.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  rentalType: PropTypes.oneOf(['hourly', 'daily']).isRequired,
};

// Map location picker component
const MapLocationPicker = ({ control, errors, setValue, watch }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const address = watch('address');
  const latitude = watch('latitude') || 57.152985;
  const longitude = watch('longitude') || 65.561989;
  
  // Initialize map
  useEffect(() => {
    // Clean up previous map instance
    const cleanupMap = () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    
    // Initialize map when container is ready
    const initMap = () => {
      try {
        if (!mapContainerRef.current) return;
        
        // Очищаем предыдущую карту перед созданием новой
        cleanupMap();
        
        // Default Tyumen coordinates
        const defaultCenter = [57.152985, 65.561989];
        const center = (!isNaN(latitude) && !isNaN(longitude)) ? 
                      [latitude, longitude] : defaultCenter;
        
        // Create Leaflet map without default attribution control
        mapRef.current = window.L.map(mapContainerRef.current, { attributionControl: false }).setView(center, 13);
        // Add custom attribution control without prefix or emojis
        window.L.control.attribution({ prefix: false }).addTo(mapRef.current);
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(mapRef.current);
        
        // Add marker if coordinates exist
        if (!isNaN(latitude) && !isNaN(longitude)) {
          markerRef.current = window.L.marker([latitude, longitude]).addTo(mapRef.current);
        }
        
        // Add click handler to map
        mapRef.current.on('click', (e) => {
          const { lat, lng } = e.latlng;
          
          // Update marker
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
          } else {
            markerRef.current = window.L.marker([lat, lng]).addTo(mapRef.current);
          }
          
          // Update form values
          setValue('latitude', lat, { shouldValidate: true });
          setValue('longitude', lng, { shouldValidate: true });
          
          // Reverse geocode to get address
          reverseGeocode([lat, lng]);
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };
    
    // Ensure Leaflet CSS is loaded
    const ensureLeafletCss = () => {
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
    };

    // Load Leaflet if not already loaded
    if (window.L) {
      ensureLeafletCss();
      initMap();
    } else {
      ensureLeafletCss();
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.async = true;
      script.onload = () => initMap();
      script.onerror = () => {
        console.error('Failed to load Leaflet script');
      };
      document.body.appendChild(script);
    }
    
    return cleanupMap;
  }, []);  // Убираем зависимости, чтобы карта инициализировалась только один раз
  
  // Обновление положения карты и маркера при изменении координат
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    
    // Проверяем, что координаты валидные
    if (!isNaN(latitude) && !isNaN(longitude)) {
      // Обновляем центр карты
      mapRef.current.setView([latitude, longitude], 13);
      
      // Обновляем или создаем маркер
      if (markerRef.current) {
        markerRef.current.setLatLng([latitude, longitude]);
      } else {
        markerRef.current = window.L.marker([latitude, longitude]).addTo(mapRef.current);
      }
    }
  }, [latitude, longitude]);
  
  // Geocode address to coordinates
  const geocodeAddress = async () => {
    if (!address) return;
    
    try {
      // Use OpenStreetMap Nominatim for geocoding
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0) {
        const item = data[0];
        const lat = parseFloat(item.lat);
        const lon = parseFloat(item.lon);
        
        // Update form values
        setValue('latitude', lat, { shouldValidate: true });
        setValue('longitude', lon, { shouldValidate: true });
        
        // Update map and marker
        if (mapRef.current) {
          mapRef.current.setView([lat, lon], 13);
          
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lon]);
          } else {
            markerRef.current = window.L.marker([lat, lon]).addTo(mapRef.current);
          }
        }
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    }
  };
  
  // Reverse geocode coordinates to address
  const reverseGeocode = async (coords) => {
    try {
      // Use OpenStreetMap Nominatim for reverse geocoding
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords[0]}&lon=${coords[1]}`);
      const data = await response.json();
      
      if (data && (data.display_name || (data.address && data.address.road))) {
        const formatted = data.display_name || `${data.address.road || ''} ${data.address.house_number || ''}`.trim();
        setValue('address', formatted, { shouldValidate: true });
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };
  
  // Update map when address changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (address && (isNaN(latitude) || isNaN(longitude))) {
        geocodeAddress();
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [address]);
  
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Координаты объекта
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Controller
            name="latitude"
            control={control}
            rules={{ required: 'Широта обязательна' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Широта"
                type="number"
                inputProps={{ step: 'any' }}
                fullWidth
                error={!!errors.latitude}
                helperText={errors.latitude?.message}
              />
            )}
          />
          <Controller
            name="longitude"
            control={control}
            rules={{ required: 'Долгота обязательна' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Долгота"
                type="number"
                inputProps={{ step: 'any' }}
                fullWidth
                error={!!errors.longitude}
                helperText={errors.longitude?.message}
              />
            )}
          />
        </Box>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Выберите местоположение на карте (кликните по карте)
        </Typography>
        <Paper 
          ref={mapContainerRef} 
          sx={{ 
            width: '100%', 
            height: 300, 
            mb: 2 
          }} 
        />
        <FormHelperText>
          Вы можете ввести адрес вручную или выбрать место на карте. Координаты будут обновлены автоматически.
        </FormHelperText>
      </Grid>
    </>
  );
};

// Common form fields component
const CommonFields = ({ control, errors, setValue, watch }) => (
  <>
    <Grid item xs={12}>
      <Controller
        name="name"
        control={control}
        rules={{ required: 'Название обязательно' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Название объекта"
            fullWidth
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        )}
      />
    </Grid>
    
    <Grid item xs={12}>
      <Controller
        name="address"
        control={control}
        rules={{ required: 'Адрес обязателен' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Адрес объекта"
            fullWidth
            error={!!errors.address}
            helperText={errors.address?.message}
          />
        )}
      />
    </Grid>
    
    <MapLocationPicker control={control} errors={errors} setValue={setValue} watch={watch} />
    
    <Grid item xs={12}>
      <Controller
        name="description"
        control={control}
        rules={{ required: 'Описание обязательно' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Описание"
            fullWidth
            multiline
            rows={4}
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        )}
      />
    </Grid>
  </>
);

CommonFields.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired
};

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isEditMode = Boolean(id);
  const [activeTab, setActiveTab] = useState(0);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Image state
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  
  // Video state
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  
  // Custom prices, promotions, additional services
  const [customPrices, setCustomPrices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  
  // State for hourly prices by guest count
  const [hourlyPrices, setHourlyPrices] = useState([]);
  
  // Fetch property data on component mount if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      console.log('Fetching property data for ID:', id);
      fetchPropertyData();
    }
    
    // Загружаем все доступные видео
    fetchAvailableVideos();
  }, [isEditMode, id]);
  
  // Загрузка всех доступных видео
  const fetchAvailableVideos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/videos', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Проверяем, что response.data - это массив, иначе используем пустой массив
      const videos = Array.isArray(response.data) ? response.data : 
                    (response.data && Array.isArray(response.data.data)) ? response.data.data : 
                    (response.data && Array.isArray(response.data.videos)) ? response.data.videos : [];
      
      console.log('Fetched available videos:', videos);
      // Если есть хотя бы одно видео, выводим его структуру для отладки
      if (videos.length > 0) {
        console.log('First video object structure:', JSON.stringify(videos[0]));
      }
      setAvailableVideos(videos);
    } catch (error) {
      console.error('Error fetching available videos:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось загрузить доступные видео',
        severity: 'error'
      });
    }
  };
  
  // Cleanup URL objects when component unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup main image preview
      if (mainImagePreview && mainImagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(mainImagePreview);
      }
      
      // Cleanup additional image previews
      additionalImagePreviews.forEach(preview => {
        if (preview.url && preview.url.startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
      
      // Cleanup video previews
      videoPreviews.forEach(preview => {
        if (preview.url && preview.url.startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [mainImagePreview, additionalImagePreviews, videoPreviews]);
  
  // Fetch property data for edit mode
  const fetchPropertyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/properties/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const data = response.data;
      
      // Debug log for image URLs
      console.log('Property data received:', {
        id: data.id,
        name: data.name,
        mainImageUrl: data.mainImageUrl,
        mainImage: data.mainImage,
        imageUrl: data.imageUrl
      });
      
      // Set form values
      setValue('name', data.name);
      setValue('description', data.description);
      setValue('address', data.address);
      
      // Set coordinates if available
      if (data.latitude && data.longitude) {
        setValue('latitude', parseFloat(data.latitude));
        setValue('longitude', parseFloat(data.longitude));
        console.log('Loaded coordinates:', data.latitude, data.longitude);
      } else {
        // Default to Tyumen center if no coordinates
        setValue('latitude', 57.152985);
        setValue('longitude', 65.561989);
        console.log('Using default Tyumen coordinates');
      }
      
      setValue('rentalType', data.rentalType);
      setValue('pricePerDay', data.pricePerDay);
      setValue('pricePerHour', data.pricePerHour);
      
      // Цены по дням недели
      setValue('mondayPrice', data.mondayPrice || data.pricePerDay);
      setValue('tuesdayPrice', data.tuesdayPrice || data.pricePerDay);
      setValue('wednesdayPrice', data.wednesdayPrice || data.pricePerDay);
      setValue('thursdayPrice', data.thursdayPrice || data.pricePerDay);
      setValue('fridayPrice', data.fridayPrice || data.pricePerDay);
      setValue('saturdayPrice', data.saturdayPrice || data.pricePerDay);
      setValue('sundayPrice', data.sundayPrice || data.pricePerDay);
      setValue('holidayPrice', data.holidayPrice || (data.pricePerDay ? data.pricePerDay * 1.5 : null));
      
      setValue('extraGuestPrice', data.extraGuestPrice || 0);
      setValue('minimumDays', data.minimumDays || 1);
      setValue('minimumHours', data.minimumHours || 2);
      setValue('isActive', data.isActive);
      setValue('checkInTime', data.checkInTime || '14:00');
      setValue('checkOutTime', data.checkOutTime || '12:00');
      
      // Загружаем поле предоплаты
      setValue('prepayment', data.prepayment || 0);
      console.log('Loaded prepayment value:', data.prepayment);
      
      // Set main image preview if exists
      // Check different possible field names for main image URL
      console.log('Setting main image preview, data:', data);
      
      if (data.mainImageUrl) {
        console.log('Using mainImageUrl:', data.mainImageUrl);
        setMainImagePreview(data.mainImageUrl);
      } else if (data.mainImage) {
        console.log('Using mainImage:', data.mainImage);
        setMainImagePreview(data.mainImage);
      } else if (data.imageUrl) {
        console.log('Using imageUrl:', data.imageUrl);
        setMainImagePreview(data.imageUrl);
      } else if (data.image) {
        console.log('Using image:', data.image);
        setMainImagePreview(data.image);
      }
      
      // Set existing images if they exist
      if (data.PropertyImages && Array.isArray(data.PropertyImages)) {
        setExistingImages(data.PropertyImages);
      }
      
      // Set existing videos if they exist
      if (data.PropertyVideos && Array.isArray(data.PropertyVideos)) {
        console.log('Property videos from API:', data.PropertyVideos);
        setExistingVideos(data.PropertyVideos);
        
        // Собираем выбранные видео из связанных видео
        const selected = [];
        data.PropertyVideos.forEach(propertyVideo => {
          console.log('Processing property video:', propertyVideo);
          
          if (propertyVideo.videoId && propertyVideo.Video) {
            console.log('Found linked video with ID:', propertyVideo.videoId, 'Video object:', propertyVideo.Video);
            // Если у нас есть связанный объект Video, используем его
            selected.push({
              id: propertyVideo.videoId,
              title: propertyVideo.Video.title,
              url: propertyVideo.Video.url, // Исправлено с videoUrl на url
              videoUrl: propertyVideo.Video.url,
              thumbnailUrl: propertyVideo.Video.thumbnailUrl, // Добавлено поле thumbnailUrl
              thumbnail: propertyVideo.Video.thumbnailUrl,
              description: propertyVideo.Video.description, // Добавлено поле description
              caption: propertyVideo.Video.description || propertyVideo.caption
            });
          } else if (propertyVideo.videoUrl) {
            // Если нет связанного видео, но есть URL
            console.log('Found video URL without linked Video object:', propertyVideo.videoUrl);
            selected.push({
              id: propertyVideo.id,
              url: propertyVideo.videoUrl,
              videoUrl: propertyVideo.videoUrl,
              thumbnail: propertyVideo.thumbnail,
              thumbnailUrl: propertyVideo.thumbnail,
              caption: propertyVideo.caption,
              title: propertyVideo.caption
            });
          }
        });
        
        if (selected.length > 0) {
          console.log('Setting selected videos:', selected);
          setSelectedVideos(selected);
        }
      }
      
      // Set custom prices if exist
      if (data.customPrices && Array.isArray(data.customPrices)) {
        setCustomPrices(data.customPrices);
      }
      
      // Set promotions if exist
      if (data.promotions && Array.isArray(data.promotions)) {
        setPromotions(data.promotions);
      }
      
      // Set additional services if exist
      if (data.additionalServices && Array.isArray(data.additionalServices)) {
        setAdditionalServices(data.additionalServices);
      }
      
      // Set hourly prices by guest count if exist
      if (data.hourlyPrices && Array.isArray(data.hourlyPrices)) {
        setHourlyPrices(data.hourlyPrices);
      } else if (data.rentalType === 'hourly' && data.pricePerHour) {
        // If no hourly prices array but has basic price, create initial price for 1 guest
        setHourlyPrices([{ guests: 1, price: parseFloat(data.pricePerHour) }]);
      }
      
      // Set external platform links
      setValue('dgisLink', data.dgisLink || '');
      setValue('yandexLink', data.yandexLink || '');
      setValue('avitoLink', data.avitoLink || '');
      setValue('vkLink', data.vkLink || '');
      setValue('instagramLink', data.instagramLink || '');
      
      console.log('Loaded external links:', {
        dgisLink: data.dgisLink,
        yandexLink: data.yandexLink,
        avitoLink: data.avitoLink,
        vkLink: data.vkLink,
        instagramLink: data.instagramLink
      });
      
      // Set active tab based on rental type
      if (data.rentalType === 'hourly') {
        setActiveTab(1);
      }
      
    } catch (error) {
      console.error('Error fetching property:', error);
      setError('Не удалось загрузить данные объекта недвижимости');
      setSnackbar({
        open: true,
        message: 'Не удалось загрузить данные объекта недвижимости',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Form state
  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      description: '',
      address: '',
      latitude: 57.152985, // Широта центра Тюмени по умолчанию
      longitude: 65.561989, // Долгота центра Тюмени по умолчанию
      rentalType: 'daily',
      pricePerDay: 0,
      pricePerHour: 0,
      mondayPrice: 0,
      tuesdayPrice: 0,
      wednesdayPrice: 0,
      thursdayPrice: 0,
      fridayPrice: 0,
      saturdayPrice: 0,
      sundayPrice: 0,
      holidayPrice: 0,
      extraGuestPrice: 0,
      minimumDays: 1,
      minimumHours: 2,
      customPrices: [],
      promotions: [],
      additionalServices: [],
      isActive: true,
      checkInTime: '14:00',
      checkOutTime: '12:00',
      prepayment: 0,
      // External platform links
      dgisLink: '',
      yandexLink: '',
      avitoLink: '',
      vkLink: '',
      instagramLink: ''
    }
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setValue('rentalType', newValue === 0 ? 'daily' : 'hourly');
  };
  
  // Handle video change
  const handleVideoChange = (e, updatedPreviews = null) => {
    // Если передан массив обновленных превью (для обновления подписей)
    if (updatedPreviews !== null) {
      setVideoPreviews(updatedPreviews);
      return;
    }

    // Обработка загрузки нового видео
    if (e && e.target && e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file) {
        // Добавляем файл в массив файлов
        setVideoFiles(prevFiles => [...prevFiles, file]);

        // Создаем URL для превью
        const videoUrl = URL.createObjectURL(file);
        setVideoPreviews(prev => [...prev, {
          url: videoUrl,
          caption: '',
          displayOrder: prev.length,
          file: file
        }]);
      }
    }
  };


  
  // Remove video
  const handleRemoveVideo = (index) => {
    // Удаляем файл и превью
    setVideoFiles(prevFiles => prevFiles.filter((_, i) => i !== index));

    // Освобождаем URL объект для предотвращения утечек памяти
    if (videoPreviews[index] && videoPreviews[index].url) {
      URL.revokeObjectURL(videoPreviews[index].url);
    }

    setVideoPreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
  };

  // Remove existing video
  const handleRemoveExistingVideo = async (videoId, index) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/properties/videos/${videoId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setExistingVideos(prevVideos => {
        const newVideos = [...prevVideos];
        newVideos.splice(index, 1);
        return newVideos;
      });

      setSnackbar({
        open: true,
        message: 'Видео успешно удалено',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing video:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось удалить видео',
        severity: 'error'
      });
    }
  };
  
  // Выбор существующего видео
  const handleSelectExistingVideo = (video, isSelected) => {
    console.log('Handling video selection:', video, 'isSelected:', isSelected);
    if (isSelected) {
      // Если видео уже выбрано, удаляем его из списка выбранных
      setSelectedVideos(prevSelected => {
        const newSelected = prevSelected.filter(v => v.id !== video.id);
        console.log('Updated selected videos after removal:', newSelected);
        return newSelected;
      });
    } else {
      // Если видео не выбрано, добавляем его в список выбранных
      setSelectedVideos(prevSelected => {
        const newSelected = [...prevSelected, video];
        console.log('Updated selected videos after addition:', newSelected);
        return newSelected;
      });
    }
  };
  
  // Handle main image change
  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle additional images change
  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Проверка на размер файлов (макс. 8MB на файл)
    const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setSnackbar({
        open: true,
        message: `Некоторые файлы слишком большие (более 50MB): ${oversizedFiles.map(f => f.name).join(', ')}`,
        severity: 'error'
      });
      return;
    }
    
    // Проверка на общее количество файлов (макс. 50 файлов)
    if (additionalImageFiles.length + files.length > 50) {
      setSnackbar({
        open: true,
        message: 'Можно загрузить максимум 50 дополнительных изображений',
        severity: 'warning'
      });
      return;
    }
    
    setAdditionalImageFiles(prevFiles => [...prevFiles, ...files]);
    
    // Generate previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAdditionalImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };
  
  // Remove additional image
  const handleRemoveAdditionalImage = (index) => {
    setAdditionalImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setAdditionalImagePreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
  };
  
  // Remove existing image
  const handleRemoveExistingImage = async (imageId, index) => {
    try {
      // Add image ID to the list of images to delete for form submission
      setImagesToDelete(prev => [...prev, imageId]);
      
      // If in edit mode, also try to delete from server immediately
      if (isEditMode) {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/properties/images/${imageId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      // Update UI
      setExistingImages(prevImages => {
        const newImages = [...prevImages];
        newImages.splice(index, 1);
        return newImages;
      });
      
      // Show notification
      setSnackbar({
        open: true,
        message: isEditMode ? 'Изображение успешно удалено' : 'Изображение будет удалено при сохранении',
        severity: isEditMode ? 'success' : 'info'
      });
    } catch (error) {
      console.error('Error removing image:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось удалить изображение',
        severity: 'error'
      });
    }
  };
  
  // Form submission handler
  const onSubmit = async (formData) => {
    setSubmitting(true);
    setError('');
    
    // Проверка на наличие изображений
    if (!isEditMode && !mainImage && additionalImageFiles.length === 0) {
      setError('Необходимо загрузить хотя бы одно изображение');
      setSnackbar({
        open: true,
        message: 'Необходимо загрузить хотя бы одно изображение',
        severity: 'error'
      });
      setSubmitting(false);
      return;
    }
    
    try {
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Не авторизован');
      }
      
      // Prepare headers with token for multipart/form-data
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      // Add custom data to formData
      formData.customPrices = customPrices;
      formData.promotions = promotions;
      formData.additionalServices = additionalServices;
      formData.hourlyPrices = hourlyPrices;
      
      // Явно включаем цены по дням недели для посуточной аренды
      if (formData.rentalType === 'daily') {
        // Убедимся, что все цены по дням недели являются числами
        formData.mondayPrice = parseInt(formData.mondayPrice) || formData.pricePerDay;
        formData.tuesdayPrice = parseInt(formData.tuesdayPrice) || formData.pricePerDay;
        formData.wednesdayPrice = parseInt(formData.wednesdayPrice) || formData.pricePerDay;
        formData.thursdayPrice = parseInt(formData.thursdayPrice) || formData.pricePerDay;
        formData.fridayPrice = parseInt(formData.fridayPrice) || formData.pricePerDay;
        formData.saturdayPrice = parseInt(formData.saturdayPrice) || formData.pricePerDay;
        formData.sundayPrice = parseInt(formData.sundayPrice) || formData.pricePerDay;
        formData.holidayPrice = parseInt(formData.holidayPrice) || Math.round(formData.pricePerDay * 1.5);
      }
      
      // Make sure latitude and longitude are properly formatted
      if (isNaN(formData.latitude) || isNaN(formData.longitude)) {
        // Default to Tyumen center if coordinates are invalid
        formData.latitude = 57.152985;
        formData.longitude = 65.561989;
        console.log('Using default Tyumen coordinates for submission');
      } else {
        console.log('Sending coordinates to server:', formData.latitude, formData.longitude);
      }
      
      // Make sure we have the correct minimum time values based on rental type
      if (formData.rentalType === 'hourly') {
        // Make sure minimumHours is set (default to 2 if not)
        formData.minimumHours = formData.minimumHours || 2;
        // Set minimumDays to null for hourly rentals
        formData.minimumDays = null;
      } else {
        // Make sure minimumDays is set (default to 1 if not)
        formData.minimumDays = formData.minimumDays || 1;
        // Set minimumHours to null for daily rentals
        formData.minimumHours = null;
      }
      
      // Преобразуем предоплату в число
      formData.prepayment = parseInt(formData.prepayment) || 0;
      console.log('Sending prepayment value to server:', formData.prepayment);
      
      // Create FormData object for file upload
      const submitData = new FormData();
      
      // Add property data as JSON string
      submitData.append('propertyData', JSON.stringify(formData));
      
      // Add main image if selected
      if (mainImage) {
        submitData.append('mainImage', mainImage);
      }
      
      // Add additional images if selected
      if (additionalImageFiles.length > 0) {
        additionalImageFiles.forEach(file => {
          submitData.append('additionalImages', file);
        });
      }
      
      // Add list of images to delete if any
      if (imagesToDelete && imagesToDelete.length > 0) {
        submitData.append('imagesToDelete', JSON.stringify(imagesToDelete));
      }
      
      // Сохраняем объект и получаем ответ
      let response;
      if (isEditMode) {
        // Update existing property
        response = await axios.put(`/api/properties/${id}`, submitData, config);
      } else {
        // Create new property
        response = await axios.post('/api/properties', submitData, config);
      }
      
      // Используем id из ответа или из параметров URL (для редактирования)
      const propertyId = response.data.id || id;
      
      // Если есть выбранные видео, связываем их с объектом недвижимости
      if (selectedVideos.length > 0) {
        try {
          console.log('Linking videos to property:', propertyId);
          console.log('Selected videos:', selectedVideos);
          console.log('Video IDs to link:', selectedVideos.map(video => video.id));
          
          // Отправляем запрос на связывание видео с объектом
          const linkResponse = await axios.post(`/api/properties/${propertyId}/link-videos`, {
            videoIds: selectedVideos.map(video => video.id)
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log('Videos linked successfully', linkResponse.data);
        } catch (videoError) {
          console.error('Error linking videos:', videoError);
          setSnackbar({
            open: true,
            message: 'Объект сохранен, но возникла ошибка при связывании видео',
            severity: 'warning'
          });
          // Продолжаем выполнение, так как основные данные объекта уже сохранены
        }
      }
      
      setSuccess(true);
      setSnackbar({
        open: true,
        message: isEditMode ? 'Объект успешно обновлен' : 'Объект успешно создан',
        severity: 'success'
      });
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/admin/properties');
      }, 1500);
      
    } catch (error) {
      console.error('Error saving property:', error);
      
      // Более детальная информация об ошибке
      let errorMessage = 'Ошибка при сохранении объекта';
      
      if (error.response) {
        // Ответ от сервера с ошибкой
        errorMessage = error.response.data?.message || `Ошибка сервера: ${error.response.status}`;
      } else if (error.request) {
        // Запрос был сделан, но ответ не получен
        errorMessage = 'Сервер не отвечает. Проверьте, запущен ли сервер.';
      } else {
        // Что-то пошло не так при настройке запроса
        errorMessage = `Ошибка: ${error.message}`;
      }
      
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Custom Prices handlers
  const addCustomPrice = (customPrice = { condition: '', price: 0, icon: '' }) => {
    setCustomPrices([...customPrices, customPrice]);
  };
  
  const removeCustomPrice = (index) => {
    const newCustomPrices = [...customPrices];
    newCustomPrices.splice(index, 1);
    setCustomPrices(newCustomPrices);
  };
  
  const updateCustomPrice = (index, field, value) => {
    const newCustomPrices = [...customPrices];
    newCustomPrices[index][field] = value;
    setCustomPrices(newCustomPrices);
  };
  
  // Promotions handlers
  const addPromotion = (promotion = { name: '', description: '', price: 0, icon: '' }) => {
    setPromotions([...promotions, promotion]);
  };
  
  const removePromotion = (index) => {
    const newPromotions = [...promotions];
    newPromotions.splice(index, 1);
    setPromotions(newPromotions);
  };
  
  const updatePromotion = (index, updatedPromotion) => {
    const newPromotions = [...promotions];
    newPromotions[index] = updatedPromotion;
    setPromotions(newPromotions);
  };
  
  // Functions for additional services
  const addService = (service) => {
    setAdditionalServices([...additionalServices, service]);
  };
  
  const updateService = (index, updatedService) => {
    const newServices = [...additionalServices];
    newServices[index] = updatedService;
    setAdditionalServices(newServices);
  };
  
  const removeService = (index) => {
    const newServices = [...additionalServices];
    newServices.splice(index, 1);
    setAdditionalServices(newServices);
  };
  
  // Алиасы для функций работы с дополнительными услугами (для совместимости)
  const addAdditionalService = addService;
  const updateAdditionalService = updateService;
  const removeAdditionalService = removeService;
  
  // Functions for hourly prices by guest count
  const addHourlyPrice = (price) => {
    setHourlyPrices([...hourlyPrices, price]);
  };
  
  const updateHourlyPrice = (index, updatedPrice) => {
    const newPrices = [...hourlyPrices];
    newPrices[index] = updatedPrice;
    setHourlyPrices(newPrices);
  };
  
  const removeHourlyPrice = (index) => {
    const newPrices = [...hourlyPrices];
    newPrices.splice(index, 1);
    setHourlyPrices(newPrices);
  };
  
  // Обработчики для изображений перемещены выше

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/admin/properties')}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          {isEditMode ? 'Редактирование объекта' : 'Добавление объекта'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Объект успешно {isEditMode ? 'обновлен' : 'добавлен'}!
            </Alert>
          )}
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Tabs for rental type */}
              <Grid item xs={12}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="Тип аренды"
                  variant="fullWidth"
                  sx={{ mb: 3 }}
                >
                  <Tab label="Посуточная аренда" />
                  <Tab label="Почасовая аренда" />
                </Tabs>
              </Grid>
              
              {/* Common fields */}
                <CommonFields control={control} errors={errors} setValue={setValue} watch={watch} />
              
              {/* Tab panels */}
              <TabPanel value={activeTab} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Цены и параметры аренды
                    </Typography>
                  </Grid>
                  
                  <PriceFields control={control} errors={errors} rentalType="daily" />
                  
                  {/* Time fields for daily rental */}
                  <TimeFields control={control} errors={errors} />
                  
                  {/* Minimum days */}
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="minimumDays"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Минимальное количество дней"
                          type="number"
                          InputProps={{ inputProps: { min: 1 } }}
                          error={!!errors.minimumDays}
                          helperText={errors.minimumDays?.message}
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Custom prices */}
                  <CustomPrices 
                    customPrices={customPrices}
                    updateCustomPrice={updateCustomPrice}
                    removeCustomPrice={removeCustomPrice}
                    addCustomPrice={addCustomPrice}
                  />
                  
                  {/* Promotions */}
                  <Promotions 
                    promotions={promotions}
                    updatePromotion={updatePromotion}
                    removePromotion={removePromotion}
                    addPromotion={addPromotion}
                  />
                  
                  {/* Additional services */}
                  <AdditionalServices 
                    services={additionalServices}
                    updateService={updateAdditionalService}
                    removeService={removeAdditionalService}
                    addService={addAdditionalService}
                  />
                  
                  {/* Images */}
                  <ImageUpload 
                    mainImagePreview={mainImagePreview}
                    additionalImagePreviews={additionalImagePreviews}
                    existingImages={existingImages}
                    handleMainImageChange={handleMainImageChange}
                    handleAdditionalImagesChange={handleAdditionalImagesChange}
                    handleRemoveAdditionalImage={handleRemoveAdditionalImage}
                    handleRemoveExistingImage={handleRemoveExistingImage}
                    isEditMode={isEditMode}
                  />

                  {/* External Links */}
<Grid item xs={12}>
  <ExternalLinks control={control} errors={errors} />
</Grid>
                  
                  {/* Videos */}
                  <VideoUpload 
                    videoPreviews={videoPreviews}
                    existingVideos={existingVideos}
                    handleVideoChange={handleVideoChange}
                    handleRemoveVideo={handleRemoveVideo}
                    handleRemoveExistingVideo={handleRemoveExistingVideo}
                    isEditMode={isEditMode}
                    availableVideos={availableVideos}
                    handleSelectExistingVideo={handleSelectExistingVideo}
                    selectedVideos={selectedVideos}
                  />
                  
                  {/* Active switch */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          )}
                        />
                      }
                      label="Активный"
                    />
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Цены и параметры аренды
                    </Typography>
                  </Grid>
                  {/* Price fields for hourly rental */}
                  <PriceFields control={control} errors={errors} rentalType="hourly" />
                  
                  {watch('rentalType') === 'hourly' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="minimumHours"
                          control={control}
                          rules={{
                            required: 'Минимальное количество часов обязательно',
                            min: { value: 1, message: 'Минимум 1 час' },
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Минимальное количество часов"
                              type="number"
                              fullWidth
                              error={!!errors.minimumHours}
                              helperText={errors.minimumHours?.message}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">ч.</InputAdornment>,
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <HourlyPricesByGuests
                          prices={hourlyPrices}
                          updatePrice={updateHourlyPrice}
                          removePrice={removeHourlyPrice}
                          addPrice={addHourlyPrice}
                        />
                      </Grid>
                    </>
                  )}
                  
                  {/* External Links */}
                  <Grid item xs={12}>
                    <ExternalLinks control={control} errors={errors} />
                  </Grid>
                  
                  {/* Custom prices */}
                  <CustomPrices 
                    customPrices={customPrices}
                    updateCustomPrice={updateCustomPrice}
                    removeCustomPrice={removeCustomPrice}
                    addCustomPrice={addCustomPrice}
                  />
                  
                  {/* Promotions */}
                  <Promotions 
                    promotions={promotions}
                    updatePromotion={updatePromotion}
                    removePromotion={removePromotion}
                    addPromotion={addPromotion}
                  />
                  
                  {/* Additional services */}
                  <AdditionalServices 
                    services={additionalServices}
                    updateService={updateAdditionalService}
                    removeService={removeAdditionalService}
                    addService={addAdditionalService}
                  />
                  
                  {/* Images */}
                  <ImageUpload 
                    mainImagePreview={mainImagePreview}
                    additionalImagePreviews={additionalImagePreviews}
                    existingImages={existingImages}
                    handleMainImageChange={handleMainImageChange}
                    handleAdditionalImagesChange={handleAdditionalImagesChange}
                    handleRemoveAdditionalImage={handleRemoveAdditionalImage}
                    handleRemoveExistingImage={handleRemoveExistingImage}
                    isEditMode={isEditMode}
                  />
                  
                  {/* Videos */}
                  <VideoUpload 
                    videoPreviews={videoPreviews}
                    existingVideos={existingVideos}
                    handleVideoChange={handleVideoChange}
                    handleRemoveVideo={handleRemoveVideo}
                    handleRemoveExistingVideo={handleRemoveExistingVideo}
                    isEditMode={isEditMode}
                    availableVideos={availableVideos}
                    handleSelectExistingVideo={handleSelectExistingVideo}
                    selectedVideos={selectedVideos}
                  />
                  
                  {/* Active switch */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          )}
                        />
                      }
                      label="Активный"
                    />
                  </Grid>
                </Grid>
              </TabPanel>
              
              {/* Form actions */}
              <FormActions 
                onCancel={() => navigate('/admin/properties')} 
                onSubmit={handleSubmit(onSubmit)} 
                isSubmitting={submitting} 
                isEditMode={isEditMode} 
              />
              
            </Grid>
          </form>
        </Paper>
      )}
    </Box>
  );
};

export default PropertyForm;
